/**
 * RegionTrendChart — oggi vs. tipico chart for the Overview hero.
 *
 * Shows the regional people-waiting curve for today overlaid on the historical
 * typical band (p25–p75) for the same day-of-week. Both series use the same
 * y-axis (people currently waiting). No forecast. No estimated wait.
 *
 * Visx pattern matches HistoryChart.tsx: size-driven, wrapped in
 * RegionTrendChartCard which uses ParentSize for responsive sizing.
 *
 * Interactive playhead: a `role="slider"` overlay (sibling to the SVG, so the
 * picture stays a non-interactive `role="img"`) lets a curious local drag — or
 * keyboard-step — across today's curve. The chart's OWN readout updates to `alle
 * HH:00: N persone` (or "nessun dato" over a gap; the big regional hero is
 * untouched), bounded to first-known→now so it never reads the future, and
 * editorial beats (busiest/quietest) reveal as the playhead passes them. On
 * release the playhead stays put with a "torna ad adesso" affordance; returning
 * to live resumes the now-dot heartbeat. Pointer, touch and keyboard reach the
 * same state; all motion is CSS, so reduced-motion degrades to instant-but-
 * functional. The snap/lookup lives in lib/scrubber.ts (bisector, unit-tested).
 */

import { useId, useRef, useState, type PointerEvent, type KeyboardEvent } from "react";
import { scaleLinear } from "@visx/scale";
import { LinePath, Area } from "@visx/shape";
import { curveMonotoneX } from "@visx/curve";
import { Group } from "@visx/group";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { GridRows } from "@visx/grid";
import { ParentSize } from "@visx/responsive";
import type { RegionTrendResponse } from "@/api/generated/model/regionTrendResponse";
import { regionTrend } from "@/lib/regionTrend";
import {
  expandToFullDay,
  contiguousBandWindow,
  bandFeatherEnds,
  bandFeatherFraction,
  bandFeatherStops,
  type TodayPoint,
  type TypicalPoint,
  type BandedPoint,
} from "@/lib/regionChart";
import { scrubAt, scrubBounds, chartAnnotations, type AnnotationKind } from "@/lib/scrubber";

// ── Scrubber copy (Italian; keyed here so DE/EN/Ladin slot in) ─────────────────

const SCRUB = {
  sliderLabel: "Esplora le ore della giornata",
  now: (v: number) => `adesso: ${v} persone`,
  at: (h: number, v: number) => `alle ${pad2(h)}:00: ${v} persone`,
  noData: (h: number) => `alle ${pad2(h)}:00: nessun dato`,
  returnToLive: "torna ad adesso",
  annotation: { busiest: "più affollato di oggi", quietest: "più tranquillo di oggi" } as Record<
    AnnotationKind,
    string
  >,
} as const;

function pad2(h: number): string {
  return String(h).padStart(2, "0");
}

// ── Types ────────────────────────────────────────────────────────────────────

interface RegionTrendChartProps {
  today: TodayPoint[];
  typical: TypicalPoint[];
  width: number;
  height: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const MARGIN = { top: 16, right: 20, bottom: 28, left: 36 } as const;
const CHART_HEIGHT = 280;
const X_TICKS = [0, 6, 12, 18] as const;

// ── Chart component ───────────────────────────────────────────────────────────

export function RegionTrendChart({ today, typical, width, height }: RegionTrendChartProps) {
  const featherMaskId = useId();
  // Scrubbed hour, or null when "live" (playhead parked at now). Ephemeral local
  // UI: never written to the URL, resets on reload/nav.
  const [scrubHour, setScrubHour] = useState<number | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  if (width <= 0) return null;

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  // Expand today to a contiguous series with null gaps for absent hours
  const todayFull = expandToFullDay(
    today
      .filter((p): p is typeof p & { waiting: number } => p.waiting !== null)
      .map((p) => ({ hourLocal: p.hourLocal, waiting: p.waiting })),
  );

  // y domain: 0 to max of all series
  const allValues: number[] = [
    ...todayFull.flatMap((p) => (p.waiting !== null ? [p.waiting] : [])),
    ...typical.flatMap((p) => [p.lo, p.avg, p.hi].filter((v): v is number => v !== null)),
  ];
  const yMax = allValues.length > 0 ? Math.max(...allValues) : 10;

  const xScale = scaleLinear({ domain: [0, 23], range: [0, innerWidth] });
  const yScale = scaleLinear({ domain: [0, yMax], range: [innerHeight, 0], nice: true });

  // Last non-null today point — the "now" annotation, and the hour we anchor
  // the band window to (see below).
  const lastToday = [...todayFull].reverse().find((p) => p.waiting !== null);

  // Clip the band to a contiguous well-sampled window so it never cliffs into a
  // vertical wedge where sample counts drop below the server's threshold. Prefer
  // the island containing "now" so the oggi-vs-tipico comparison is never the
  // one dropped when history matures into several islands.
  const bandWindow = contiguousBandWindow(typical, lastToday?.hourLocal);
  const bandVisible = bandWindow.length >= 2;
  const firstBandHour = bandWindow[0]?.hourLocal ?? 0;
  const lastBandHour = bandWindow[bandWindow.length - 1]?.hourLocal ?? 0;
  const bandX1 = xScale(firstBandHour);
  const bandX2 = xScale(lastBandHour);
  // Feather only the ends that were clipped due to sparsity — never a fully
  // sampled day boundary (that would imply uncertainty that isn't there).
  const featherEnds = bandFeatherEnds(typical, bandWindow);
  const needsFeather = featherEnds.left || featherEnds.right;
  const featherStops = bandFeatherStops(
    bandFeatherFraction(lastBandHour - firstBandHour),
    featherEnds.left,
    featherEnds.right,
  );

  // Use the shared regionTrend helper for the callout annotation
  const trend = regionTrend(todayFull, typical);
  const trendPctText =
    trend.pct !== null ? (trend.pct >= 0 ? `+${trend.pct}%` : `${trend.pct}%`) : null;

  // ── Scrubber derivation ──────────────────────────────────────────────────────
  const bounds = scrubBounds(todayFull);
  const scrubbing = scrubHour !== null;
  // Effective playhead hour: the scrubbed hour, else "now". Clamped into range.
  const effectiveHour = bounds
    ? Math.min(bounds.nowHour, Math.max(bounds.minHour, scrubHour ?? bounds.nowHour))
    : (lastToday?.hourLocal ?? 0);
  const reading = bounds
    ? scrubAt(todayFull, effectiveHour, bounds.minHour, bounds.nowHour)
    : { hour: effectiveHour, value: lastToday?.waiting ?? null };
  const playheadX = xScale(reading.hour);
  const annotations = chartAnnotations(todayFull);
  const activeAnnotation = annotations.find((a) => a.hour === reading.hour) ?? null;

  // The readout that becomes the slider's aria-valuetext + on-chart callout.
  const readoutText = scrubbing
    ? reading.value !== null
      ? SCRUB.at(reading.hour, reading.value)
      : SCRUB.noData(reading.hour)
    : reading.value !== null
      ? SCRUB.now(reading.value)
      : SCRUB.noData(reading.hour);

  // ── Pointer + keyboard handlers ──────────────────────────────────────────────
  // Map a client x onto the plot (the overlay covers exactly the inner plot), snap
  // to the nearest sampled hour, and park at "live" when it lands back on now.
  const scrubToClientX = (clientX: number) => {
    const el = sliderRef.current;
    if (!el || !bounds) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const rawHour = ((clientX - rect.left) / rect.width) * 23;
    const snapped = scrubAt(todayFull, rawHour, bounds.minHour, bounds.nowHour);
    setScrubHour(snapped.hour === bounds.nowHour ? null : snapped.hour);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!bounds) return;
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    scrubToClientX(e.clientX);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (draggingRef.current) scrubToClientX(e.clientX);
  };
  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId))
      e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const stepTo = (hour: number) => {
    if (!bounds) return;
    const clamped = Math.min(bounds.nowHour, Math.max(bounds.minHour, hour));
    setScrubHour(clamped === bounds.nowHour ? null : clamped);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!bounds) return;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowDown":
        e.preventDefault();
        stepTo(effectiveHour - 1);
        break;
      case "ArrowRight":
      case "ArrowUp":
        e.preventDefault();
        stepTo(effectiveHour + 1);
        break;
      case "Home":
        e.preventDefault();
        stepTo(bounds.minHour);
        break;
      case "End":
        e.preventDefault();
        setScrubHour(null);
        break;
    }
  };

  const returnToLive = () => {
    setScrubHour(null);
    sliderRef.current?.focus();
  };

  // Annotation label position: anchor to the LEFT of the playhead when it sits in
  // the right half of the day, so the callout never runs off the edge.
  const labelPastMid = reading.hour > 12;
  const labelX = labelPastMid ? playheadX - 8 : playheadX + 8;
  const labelAnchor = labelPastMid ? "end" : "start";

  return (
    <div className="region-trend-chart-wrap" style={{ position: "relative", width, height }}>
      <svg
        className="region-trend-chart"
        width={width}
        height={height}
        role="img"
        aria-label="Grafico persone in attesa oggi vs. tipico nella regione Alto Adige"
      >
        {/* Feather mask: fades the band + typical line to transparent at any
          sparsity-clipped end, so the window terminates gracefully (reads as
          "less certain") instead of cliffing into a vertical wedge. Only
          rendered when at least one end actually needs feathering. */}
        {bandVisible && needsFeather && (
          <defs>
            {/* Coordinates are the MARGIN-translated inner space, because the mask
              is referenced by a <g> nested inside the <Group left/top> below. */}
            <linearGradient
              id={`${featherMaskId}-grad`}
              gradientUnits="userSpaceOnUse"
              x1={bandX1}
              x2={bandX2}
              y1={0}
              y2={0}
            >
              {featherStops.map((s) => (
                <stop key={s.offset} offset={s.offset} stopColor="#fff" stopOpacity={s.opacity} />
              ))}
            </linearGradient>
            <mask id={featherMaskId}>
              <rect
                x={-MARGIN.left}
                y={-MARGIN.top}
                width={width}
                height={height}
                fill={`url(#${featherMaskId}-grad)`}
              />
            </mask>
          </defs>
        )}

        <Group left={MARGIN.left} top={MARGIN.top}>
          {/* Grid */}
          <GridRows
            scale={yScale}
            width={innerWidth}
            numTicks={4}
            stroke="var(--hairline)"
            strokeOpacity={0.7}
          />

          {/* Typical band (p25–p75) + avg line, clipped to the contiguous
            well-sampled window and feathered at both ends via the mask. */}
          {bandVisible && (
            <Group mask={needsFeather ? `url(#${featherMaskId})` : undefined}>
              <Area<BandedPoint>
                data={bandWindow}
                curve={curveMonotoneX}
                x={(p) => xScale(p.hourLocal)}
                y0={(p) => yScale(p.lo)}
                y1={(p) => yScale(p.hi)}
                fill="var(--band)"
                className="bandfill anim-fade"
              />
              <LinePath<BandedPoint>
                data={bandWindow}
                curve={curveMonotoneX}
                x={(p) => xScale(p.hourLocal)}
                y={(p) => yScale(p.avg)}
                stroke="var(--band-line)"
                strokeWidth={1.5}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </Group>
          )}

          {/* Today line — gaps where waiting is null (B1 fix) */}
          {todayFull.length > 0 && (
            <LinePath<TodayPoint>
              data={todayFull}
              curve={curveMonotoneX}
              defined={(p) => p.waiting !== null}
              x={(p) => xScale(p.hourLocal)}
              y={(p) => yScale(p.waiting ?? 0)}
              stroke="var(--pulse)"
              strokeWidth={2.6}
              strokeLinejoin="round"
              strokeLinecap="round"
              className="todayline"
              pathLength={1}
              strokeDasharray={1}
              strokeDashoffset={1}
            />
          )}

          {/* Playhead vertical guide — the draggable/keyboard scrub position. Only
            shown once there's a scrubbable range; more prominent while scrubbing. */}
          {bounds !== null && (
            <line
              className="scrubline"
              data-scrubbing={scrubbing ? "1" : undefined}
              x1={playheadX}
              x2={playheadX}
              y1={0}
              y2={innerHeight}
            />
          )}

          {/* Now marker (dot + ring). The ring beats while live and pauses while
            scrubbing, resuming when the playhead returns to now (A2-4). */}
          {lastToday !== undefined && lastToday.waiting !== null && (
            <>
              <circle
                className="nowring"
                cx={xScale(lastToday.hourLocal)}
                cy={yScale(lastToday.waiting)}
                r={6}
                fill="none"
                stroke="var(--pulse)"
                strokeWidth={1.5}
                style={{
                  animation: `nowbeat var(--beat-dur, 1.7s) ease-out infinite`,
                  animationPlayState: scrubbing ? "paused" : "running",
                }}
              />
              <circle
                cx={xScale(lastToday.hourLocal)}
                cy={yScale(lastToday.waiting)}
                r={4}
                fill="var(--pulse)"
              />
            </>
          )}

          {/* Playhead value dot — sits on today's curve at the scrubbed hour (absent
            over a gap, where there's no honest value to point at). */}
          {reading.value !== null && (
            <circle
              className="scrubdot"
              cx={playheadX}
              cy={yScale(reading.value)}
              r={4}
              fill="var(--pulse)"
              stroke="var(--card)"
              strokeWidth={1.5}
            />
          )}

          {/* Readout callout — the chart's own annotation (never the big hero). Reads
            "adesso: N" when live, "alle HH:00: N" (or "nessun dato") when scrubbed. */}
          {bounds !== null && (
            <>
              <text
                x={labelX}
                y={(reading.value !== null ? yScale(reading.value) : 16) - 14}
                textAnchor={labelAnchor}
                fontSize={11}
                fontWeight={600}
                fill="var(--ink)"
                fontFamily="var(--font-sans)"
                className="anim-fade"
              >
                {readoutText}
              </text>
              {!scrubbing && trendPctText !== null && reading.value !== null && (
                <text
                  x={labelX}
                  y={yScale(reading.value) - 2}
                  textAnchor={labelAnchor}
                  fontSize={11}
                  fontWeight={600}
                  fill="var(--pulse)"
                  fontFamily="var(--font-sans)"
                  className="anim-fade"
                >
                  {trendPctText}
                </text>
              )}
            </>
          )}

          {/* Contextual editorial beat — revealed only while the playhead is ON the
            busiest/quietest hour (not all at once). Sits below the axis top. */}
          {scrubbing && activeAnnotation !== null && (
            <text
              className="scrub-annotation"
              x={labelX}
              y={reading.value !== null ? yScale(reading.value) + 16 : 30}
              textAnchor={labelAnchor}
              fontSize={10.5}
              fontWeight={600}
              fill="var(--violet)"
              fontFamily="var(--font-sans)"
            >
              {SCRUB.annotation[activeAnnotation.kind]}
            </text>
          )}

          {/* Axes */}
          <AxisLeft
            scale={yScale}
            numTicks={4}
            hideAxisLine
            tickLength={4}
            tickFormat={(v) => `${Math.round(v.valueOf())}`}
            tickClassName="history-chart__tick"
          />
          <AxisBottom
            scale={xScale}
            top={innerHeight}
            hideAxisLine
            tickLength={4}
            tickValues={[...X_TICKS]}
            tickFormat={(v) => String(v.valueOf()).padStart(2, "0")}
            tickClassName="history-chart__tick"
          />
        </Group>
      </svg>

      {/* Scrub control: a focusable slider layered over the plot. Kept OUTSIDE the
          role="img" SVG so the picture stays non-interactive and the control is a
          first-class, keyboard-operable widget. Covers exactly the inner plot. */}
      {bounds !== null && (
        <div
          ref={sliderRef}
          className="region-scrubber"
          role="slider"
          tabIndex={0}
          aria-label={SCRUB.sliderLabel}
          aria-valuemin={bounds.minHour}
          aria-valuemax={bounds.nowHour}
          aria-valuenow={reading.hour}
          aria-valuetext={readoutText}
          style={{
            position: "absolute",
            left: MARGIN.left,
            top: MARGIN.top,
            width: innerWidth,
            height: innerHeight,
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onKeyDown}
        />
      )}

      {scrubbing && (
        <button type="button" className="region-scrubber__reset" onClick={returnToLive}>
          {SCRUB.returnToLive}
        </button>
      )}
    </div>
  );
}

// ── Card wrapper ──────────────────────────────────────────────────────────────

interface RegionTrendChartCardProps {
  trend: RegionTrendResponse | undefined;
  isPending: boolean;
  isError: boolean;
}

export function RegionTrendChartCard({ trend, isPending, isError }: RegionTrendChartCardProps) {
  // A band renders only when there's a contiguous well-sampled window of ≥2
  // hours — match the chart exactly (same now-preferred island) so the
  // legend/sparse-note reflect what's actually drawn.
  const nowHour = trend
    ? [...trend.today].reverse().find((p) => p.waiting !== null)?.hourLocal
    : undefined;
  const bandVisible =
    trend !== undefined && contiguousBandWindow(trend.typical, nowHour).length >= 2;
  const bandSparse = trend !== undefined && trend.typical.length > 0 && !bandVisible;

  return (
    <div className="region-chart-card">
      <div>
        <p className="region-chart__eyebrow">OGGI VS. TIPICO</p>
        <h2 className="region-chart__h2">Persone in attesa nel corso della giornata</h2>
      </div>

      {isPending && <div className="region-chart__skeleton" aria-hidden="true" />}

      {isError && !isPending && (
        <p className="region-chart__error" role="alert">
          Impossibile caricare il grafico andamento.
        </p>
      )}

      {!isPending && !isError && trend !== undefined && trend.today.length === 0 && (
        <p className="region-chart__empty">Dati non ancora disponibili per oggi.</p>
      )}

      {!isPending && !isError && trend !== undefined && trend.today.length > 0 && (
        <>
          <div className="region-chart__canvas">
            <ParentSize>
              {({ width }) => (
                <RegionTrendChart
                  today={trend.today}
                  typical={trend.typical}
                  width={width}
                  height={CHART_HEIGHT}
                />
              )}
            </ParentSize>
          </div>

          {/* Legend */}
          <ul className="region-chart__legend" aria-label="Legenda grafico">
            <li className="region-chart__legend-item">
              <span className="region-chart__legend-line" aria-hidden="true" />
              Oggi
            </li>
            {bandVisible && (
              <li className="region-chart__legend-item">
                <span className="region-chart__legend-band" aria-hidden="true" />
                Intervallo tipico
              </li>
            )}
          </ul>
        </>
      )}

      {bandSparse && (
        <p className="region-chart__sparse-note">
          Il confronto con il tipico si costruisce man mano che lo storico cresce — tornerà nei
          prossimi giorni.
        </p>
      )}
    </div>
  );
}
